import React, { useState } from 'react';
import moment from 'moment-timezone';
import { isArray } from 'lodash';

// No static import of AllReportsPDF - loaded dynamically on download
import { useReportSelection } from './ReportSelectionContext';

function ExportPdfButton({
  client,
  queryBuilder,
  searchParams,
}) {
  const { selectedReports, clearSelection } = useReportSelection();
  const [loading, setLoading] = useState(false);

  const hasSelection = selectedReports.length > 0;

  const now = moment();
  let name = `${
    now.clone().subtract(1, 'month').format('DD.MM.YYYY.')
  }-${
    now.clone().format('DD.MM.YYYY.')
  }`;
  if (searchParams.periodRange) {
    name = searchParams.periodRange.split(',').map(date => (
      moment(date).format('DD.MM.YYYY')
    )).join('-');
  }

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      let data;

      if (hasSelection) {
        data = {
          data: selectedReports,
          name: `${name} (${selectedReports.length})`,
        };
      } else {
        const query = queryBuilder.build();
        query.limit = 1000000;
        delete query.skip;

        const response = await client.request({
          // eslint-disable-next-line max-len
          url: `/reports?query=${encodeURIComponent(JSON.stringify(query))}`,
          method: 'get',
        });

        const responseBody = response.data || response;
        data = {
          data: responseBody.data || responseBody,
          name,
        };
      }

      // Dynamic import + imperative PDF generation
      // eslint-disable-next-line max-len
      const [{ pdf }, { default: ReportDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../components/PrintPDF/AllReportsPDF'),
      ]);
      const blob = await pdf(
        React.createElement(ReportDocument, { data })
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate PDF:', error);
    }
    setLoading(false);
  };

  // Build button label with selection count
  let buttonLabel = 'Pripremi Izveštaj';
  if (loading) {
    buttonLabel = 'Pripremam...';
  } else if (hasSelection) {
    buttonLabel = `Pripremi Izveštaj (${selectedReports.length})`;
  }

  return (
    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
      {hasSelection && (
        <button
          type="button"
          className="ant-btn ant-btn-primary ant-btn-background-ghost"
          onClick={clearSelection}
        >
          Očisti
        </button>
      )}
      <button
        type="button"
        className="ant-btn ant-btn-primary ant-btn-background-ghost"
        style={{ flex: 1 }}
        disabled={loading}
        onClick={handleClick}
      >
        <i className="anticon anticon-download" />
        <span>{buttonLabel}</span>
      </button>
    </div>
  );
}

export const filterPeriodRange = ['FilterField', {
  id: 'periodRange',
  label: null,
  section: 'top',
  col: 24,
  placeholder: ['Od Datuma', 'Do Datuma'],
  propsFromPage: props => ({
    searchParams: props.searchParams,
    history: props.history,
  }),
  ranges: {
    Sve: [
      moment(0),
      moment(),
    ],
    ...[
      ['Danas', 0],
      // ['Last 7 Days', 7],
      // ['Last 30 Days', 30],
      // ['Last 60 Days', 60],
      // ['Last 90 Days', 90],
      // ['Last 180 Days', 180],
    ].reduce(
      (agr, [label, daysOffset = null]) => {
        agr[label] = () => {
          const now = moment();
          return [
            now.clone().subtract(Math.max(0, daysOffset - 1), 'days'),
            now,
          ];
        };
        return agr;
      },
      {},
    ),
    'Pre 6 meseci': [
      moment().subtract(6, 'month'),
      moment().subtract(6, 'month'),
    ],
    'Pre 1 godine': [
      moment().subtract(12, 'month'),
      moment().subtract(12, 'month'),
    ],
    'Pre 2 godine': [
      moment().subtract(24, 'month'),
      moment().subtract(24, 'month'),
    ],
    'Ova nedelja pre 6 meseci': [
      moment().subtract(6, 'month').startOf('week'),
      moment().subtract(6, 'month').endOf('week'),
    ],
    'Ova nedelja pre 1 godine': [
      moment().subtract(1, 'year').startOf('week'),
      moment().subtract(1, 'year').endOf('week'),
    ],
    'Ova nedelja pre 2 godine': [
      moment().subtract(2, 'year').startOf('week'),
      moment().subtract(2, 'year').endOf('week'),
    ],
    ...[
      // ['Week', 'week'],
      // ['Month', 'month'],
      ['Godine', 'year'],
    ].reduce(
      (agr, [label, value]) => {
        agr[`Ove ${label}`] = [
          moment().startOf(value),
          moment().endOf(value),
        ];
        agr[`Prošle ${label}`] = [
          moment().subtract(1, value).startOf(value),
          moment().subtract(1, value).endOf(value),
        ];
        return agr;
      },
      {},
    ),
  },
  buildQuery: (value, builder, filter) => {
    const now = moment();
    const isSideB = filter.id.indexOf('b.') === 0
    const whereKey = isSideB ? 'whereB' : 'where';
    const dateKey = 'createdAt';
    if (
      value
      && value.length
    ) {
      const values = (
        value
        .split(',')
        .map(dateString => (
          moment(dateString, 'YYYY-MM-DD')
          // .hour(now.hour())
          // .minute(now.minute())
          // .second(now.second())
          // .millisecond(now.millisecond())
        ))
      );
      if (
        values.length === 2
        && values[0] && values[0].isValid()
        && values[1] && values[1].isValid()
      ) {
        builder.add(
          whereKey,
          {
            [dateKey]: {
              GTE: values[0].startOf('day').toJSON(),
              LTE: values[1].endOf('day').toJSON(),
            },
          },
        );
        return;
      }
    }
    builder.add(
      whereKey,
      {
        [dateKey]: {
          GTE: (
            filter.defaultDateFrom
            ? moment(filter.defaultDateFrom).toJSON()
            : now.clone().subtract(1, 'year').toJSON()
          ),
          LTE: (
            filter.defaultDateTo
            ? moment(filter.defaultDateTo).toJSON()
            : now.endOf('day').toJSON()
          ),
        },
      },
    );
  },
  getValueForField: (value, props) => {
    const now = moment();
    if (value && value.length) {
      const values = (
        value
        .split(',')
        .map(dateString => (
          moment(dateString, 'YYYY-MM-DD')
          .hour(now.hour())
          .minute(now.minute())
          .second(now.second())
          .millisecond(now.millisecond())
        ))
      );
      if (
        values.length === 2
        && values[0] && values[0].isValid()
        && values[1] && values[1].isValid()
      ) {
        return values;
      }
    }
    return [
      (
          props.defaultDateFrom
        ? moment(props.defaultDateFrom)
        : now.clone().subtract(1, 'year')
      ),
      (
          props.defaultDateTo
        ? moment(props.defaultDateTo)
        : now.startOf('day')
      ),
    ];
  },
  getValueForQuery: (value) => {
    if (
      Array.isArray(value)
      && value.length === 2
      && value[0] && value[0].isValid()
      && value[1] && value[1].isValid()
    ) {
      // const now = moment();
      return `${
        value[0].format('YYYY-MM-DD')
      },${
        value[1].format('YYYY-MM-DD')
      }`;
    }
    return undefined;
  },
  // skip: [
  //   props => props.searchParams['period-span'] !== 'custom',
  // ],
  field: ['FieldDateRangePicker', {
    id: 'periodRange',
    name: 'periodRage',
    format: 'll',
    showToday: false,
    separator: '-',
    // skip: [
    //   props => props.searchParams?.['period-span'] !== 'custom',
    // ],
  }],
}];

export const filterReportSearch = ['FilterField', {
  id: 'search',
  label: null,
  section: 'top',
  col: 12,
  searchPaths: ['name', 'customerName'],
  buildQuery: (value, builder, props) => value && builder.add(
    'where',
    {
      OR: props.searchPaths.map(searchPath => ({
        [searchPath]: { REGEX: value, OPTIONS: 'i' },
      })),
    }
  ),
  getValueForField: value => value || '',
  getValueForQuery: (value) => {
    value = !value
      ? undefined
      : value.target
        ? value.target.value
        : value;
    return !value || !value.length
      ? undefined
      : value;
  },
  field: ['FieldText', {
    name: 'search',
    placeholder: 'Pretraga',
  }],
}];

export const filterCustomer = ['FilterField', {
  id: 'customer',
  label: null,
  section: 'top',
  col: 24,
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => value && value.length && builder.add(
    'where',
    { customer: { IN: isArray(value) ? value : [value] } },
  ),
  getValueForField: value => value || undefined,
  field: ['FieldConnectionSelect', {
    name: 'customer',
    placeholder: 'Filtriraj po kupcu',
    url: '/customers',
    allowClear: true,
    searchPaths: ['name', 'code'],
    getChoiceLabel: choice => `${choice.code} ${choice.name}`,
  }],
}];

export const filterPagination20 = ['FilterPagination', {
  id: 'pagination',
  section: 'bottom',
  defaultPageSize: 20,
}];

export const filterExportPdf = ['FilterField', {
  id: 'export-pdf',
  name: 'exportPdf',
  label: null,
  section: 'top',
  col: 24,
  buildQuery: () => { },
  propsFromPage: ({ searchParams, client, queryBuilder }) => ({
    searchParams,
    client,
    queryBuilder,
  }),
  field: ['Action', {
    name: 'download-report',
    title: 'Preuzmi',
    ghost: true,
    icon: 'download',
    col: 24,
    style: { width: '100%' },
    renderAction: (props) => (
      <ExportPdfButton
        client={props.client}
        queryBuilder={props.queryBuilder}
        searchParams={props.searchParams}
      />
    ),
  }],
}];
